import React, { useState, useRef, useEffect } from 'react';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';

export interface VoiceMemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (memo: {
    mediaUrl: string;
    mediaTitle: string;
    transcript: string;
    durationSeconds: number;
    storagePath: string;
    format: string;
  }) => void;
  coachId: string;
  coachName: string;
  patientId: string;
  patientName: string;
  domainId: string;
  domainName?: string;
  initialTitle?: string;
  initialTranscript?: string;
  initialMediaUrl?: string;
  initialStoragePath?: string;
  initialDuration?: number;
}

export const VoiceMemoModal: React.FC<VoiceMemoModalProps> = ({
  isOpen,
  onClose,
  onSave,
  coachId,
  coachName,
  patientId,
  patientName,
  domainId,
  domainName = 'Mindful Habit',
  initialTitle = 'Therapist Supportive Voice Memo',
  initialTranscript = '',
  initialMediaUrl,
  initialStoragePath,
  initialDuration = 0,
}) => {
  const [activeTab, setActiveTab] = useState<'record' | 'upload'>('record');
  const [memoTitle, setMemoTitle] = useState(initialTitle);
  const [transcript, setTranscript] = useState(initialTranscript);
  const [isDragOver, setIsDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    isRecording,
    isPaused,
    recordingDuration,
    audioBlob,
    audioUrl,
    validationResult,
    error,
    isUploading,
    uploadProgress,
    mimeConfig,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
    handleFileSelect,
    clearAudio,
    uploadAudio,
  } = useAudioRecorder();

  useEffect(() => {
    if (isOpen) {
      setMemoTitle(initialTitle);
      setTranscript(initialTranscript);
      clearAudio();
    }
  }, [isOpen, initialTitle, initialTranscript, clearAudio]);

  if (!isOpen) return null;

  const formatSeconds = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      await handleFileSelect(file);
    }
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      await handleFileSelect(file);
    }
  };

  const handleSaveAndAssign = async () => {
    try {
      if (audioBlob) {
        // Upload newly recorded or attached audio
        const result = await uploadAudio({
          coachId,
          coachName,
          patientId,
          domainId,
          memoTitle: memoTitle.trim() || 'Therapist Supportive Voice Memo',
        });

        onSave({
          mediaUrl: result.downloadUrl,
          mediaTitle: memoTitle.trim() || 'Therapist Supportive Voice Memo',
          transcript: transcript.trim(),
          durationSeconds: result.durationSeconds,
          storagePath: result.storagePath,
          format: result.format,
        });
      } else if (initialMediaUrl) {
        // Keep existing memo with updated metadata
        onSave({
          mediaUrl: initialMediaUrl,
          mediaTitle: memoTitle.trim() || 'Therapist Supportive Voice Memo',
          transcript: transcript.trim(),
          durationSeconds: initialDuration,
          storagePath: initialStoragePath || '',
          format: 'm4a',
        });
      }
      onClose();
    } catch {
      // Error handled by hook state
    }
  };

  const hasPlayableAudio = Boolean(audioUrl || initialMediaUrl);
  const activeAudioUrl = audioUrl || initialMediaUrl;
  const isSaveDisabled = (!audioBlob && !initialMediaUrl) || isUploading || isRecording || (validationResult && !validationResult.isValid);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isRecording && !isUploading) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="voice-memo-modal-title"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '620px',
          maxHeight: '92vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #E2E8F0',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #E2E8F0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#F8FAFC',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
          }}
        >
          <div>
            <h2
              id="voice-memo-modal-title"
              style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <span>🎙️</span> Record or Attach Supportive Voice Memo
            </h2>
            <div style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>
              Paired Client: <strong style={{ color: '#1E293B' }}>{patientName}</strong> • Domain: <strong style={{ color: '#1E293B' }}>{domainName}</strong>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isRecording || isUploading}
            aria-label="Close modal"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '20px',
              cursor: isRecording || isUploading ? 'not-allowed' : 'pointer',
              color: '#64748B',
              padding: '4px 8px',
              borderRadius: '6px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Tab Switcher */}
        <div style={{ display: 'flex', borderBottom: '1px solid #E2E8F0', backgroundColor: '#F1F5F9' }}>
          <button
            onClick={() => setActiveTab('record')}
            disabled={isRecording}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              backgroundColor: activeTab === 'record' ? '#FFFFFF' : 'transparent',
              color: activeTab === 'record' ? '#0F172A' : '#64748B',
              fontWeight: activeTab === 'record' ? 700 : 500,
              fontSize: '13px',
              cursor: 'pointer',
              borderBottom: activeTab === 'record' ? '2px solid #2563EB' : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            🎙️ Live Browser Recording
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            disabled={isRecording}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              backgroundColor: activeTab === 'upload' ? '#FFFFFF' : 'transparent',
              color: activeTab === 'upload' ? '#0F172A' : '#64748B',
              fontWeight: activeTab === 'upload' ? 700 : 500,
              fontSize: '13px',
              cursor: 'pointer',
              borderBottom: activeTab === 'upload' ? '2px solid #2563EB' : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            📁 Upload Audio File
          </button>
        </div>

        {/* Body Content */}
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,.m4a,.mp3,.wav,.webm,.aac,.ogg"
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />
          {/* TAB 1: Live Recording */}
          {activeTab === 'record' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '12px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>Codec Negotiation:</span>
                <span style={{ fontWeight: 600, color: '#0F172A', backgroundColor: '#E2E8F0', padding: '2px 8px', borderRadius: '4px' }}>
                  {mimeConfig?.mimeType ? mimeConfig.mimeType : 'Dynamic (iOS AAC / WebM Priority)'}
                </span>
                {mimeConfig?.isIosPlayable && (
                  <span style={{ fontSize: '11px', color: '#16A34A', fontWeight: 700 }}>✓ Direct iOS AVPlayer Playable</span>
                )}
              </div>

              {/* Timer Display */}
              <div style={{ fontSize: '36px', fontWeight: 800, fontFamily: 'monospace', color: isRecording ? '#DC2626' : '#1E293B', letterSpacing: '1px' }}>
                {formatSeconds(recordingDuration)}
              </div>

              {/* Pulse animation when recording */}
              {isRecording && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '24px' }}>
                  {[40, 75, 100, 60, 90, 45, 80, 55].map((height, i) => (
                    <div
                      key={i}
                      style={{
                        width: '4px',
                        height: isPaused ? '8px' : `${height}%`,
                        backgroundColor: isPaused ? '#94A3B8' : '#EF4444',
                        borderRadius: '2px',
                        transition: 'height 0.15s ease',
                      }}
                    />
                  ))}
                  <span style={{ fontSize: '11px', fontWeight: 600, color: isPaused ? '#64748B' : '#DC2626', marginLeft: '6px' }}>
                    {isPaused ? 'PAUSED' : 'RECORDING'}
                  </span>
                </div>
              )}

              {/* Recording Controls */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                {!isRecording ? (
                  <button
                    onClick={startRecording}
                    style={{
                      backgroundColor: '#DC2626',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)',
                    }}
                  >
                    <span>🔴</span> Start Recording
                  </button>
                ) : (
                  <>
                    {!isPaused ? (
                      <button
                        onClick={pauseRecording}
                        style={{
                          backgroundColor: '#F1F5F9',
                          color: '#334155',
                          border: '1px solid #CBD5E1',
                          borderRadius: '8px',
                          padding: '10px 16px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        ⏸️ Pause
                      </button>
                    ) : (
                      <button
                        onClick={resumeRecording}
                        style={{
                          backgroundColor: '#E0F2FE',
                          color: '#0369A1',
                          border: '1px solid #BAE6FD',
                          borderRadius: '8px',
                          padding: '10px 16px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        ▶️ Resume
                      </button>
                    )}

                    <button
                      onClick={stopRecording}
                      style={{
                        backgroundColor: '#16A34A',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(22, 163, 74, 0.2)',
                      }}
                    >
                      ⏹️ Stop & Review
                    </button>

                    <button
                      onClick={cancelRecording}
                      style={{
                        backgroundColor: '#FEE2E2',
                        color: '#DC2626',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 14px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Discard
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* TAB 2: File Upload */}
          {activeTab === 'upload' && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: isDragOver ? '2px dashed #2563EB' : '2px dashed #CBD5E1',
                backgroundColor: isDragOver ? '#EFF6FF' : '#F8FAFC',
                borderRadius: '12px',
                padding: '32px 20px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📁</div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#1E293B' }}>
                Drag & drop voice memo here, or <span style={{ color: '#2563EB' }}>browse files</span>
              </div>
              <div style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>
                Supported formats: M4A, MP3, WAV, AAC, WebM (Min size: 1 KB)
              </div>
            </div>
          )}

          {/* Validation & File Details Badge */}
          {validationResult && (
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: validationResult.isValid ? '#F0FDF4' : '#FEF2F2',
                border: `1px solid ${validationResult.isValid ? '#BBF7D0' : '#FECACA'}`,
                color: validationResult.isValid ? '#166534' : '#991B1B',
              }}
            >
              <div>
                <strong>{validationResult.isValid ? '✓ Audio Validated' : '⚠️ Validation Failed'}</strong>: Container ({validationResult.detectedFormat.toUpperCase()}), Size: {(validationResult.sizeBytes / 1024).toFixed(1)} KB, Duration: {validationResult.durationSeconds > 0 ? `${validationResult.durationSeconds.toFixed(1)}s` : 'Unknown'}
              </div>
              <button
                onClick={clearAudio}
                style={{
                  background: 'none',
                  border: 'none',
                  color: validationResult.isValid ? '#166534' : '#991B1B',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontSize: '11px',
                }}
              >
                Clear
              </button>
            </div>
          )}

          {/* Error Message */}
          {error && !validationResult && (
            <div style={{ padding: '12px 16px', borderRadius: '8px', fontSize: '12px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }}>
              <strong>Error</strong>: {error}
            </div>
          )}

          {/* Audio Player Preview */}
          {hasPlayableAudio && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', backgroundColor: '#F1F5F9', padding: '14px', borderRadius: '10px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                🎧 Audio Preview Player
              </div>
              <audio controls src={activeAudioUrl || undefined} style={{ width: '100%' }} />
            </div>
          )}

          {/* Memo Title & Transcript Inputs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                Memo Title
              </label>
              <input
                type="text"
                value={memoTitle}
                onChange={(e) => setMemoTitle(e.target.value)}
                placeholder="e.g. 45s Urge Surfing Grounding Memo"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #CBD5E1',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                Live Synchronized Transcript & Reassurance Note (Optional)
              </label>
              <textarea
                rows={3}
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                placeholder="Take a slow breath. Notice the physical sensation without judgment. You are safe..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #CBD5E1',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* Upload Progress Bar */}
          {isUploading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#64748B' }}>
                <span>Uploading voice memo to Firebase Storage...</span>
                <span>{uploadProgress.toFixed(0)}%</span>
              </div>
              <div style={{ width: '100%', height: '6px', backgroundColor: '#E2E8F0', borderRadius: '3px', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${uploadProgress}%`,
                    height: '100%',
                    backgroundColor: '#2563EB',
                    transition: 'width 0.2s ease',
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #E2E8F0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            backgroundColor: '#F8FAFC',
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px',
          }}
        >
          <button
            onClick={onClose}
            disabled={isRecording || isUploading}
            style={{
              padding: '8px 16px',
              backgroundColor: '#FFFFFF',
              border: '1px solid #CBD5E1',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              color: '#475569',
              cursor: isRecording || isUploading ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleSaveAndAssign}
            disabled={isSaveDisabled}
            style={{
              padding: '8px 20px',
              backgroundColor: isSaveDisabled ? '#94A3B8' : '#2563EB',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 700,
              color: '#FFFFFF',
              cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: isSaveDisabled ? 'none' : '0 2px 4px rgba(37, 99, 235, 0.25)',
            }}
          >
            {isUploading ? 'Uploading...' : '☁️ Upload & Assign to Patient'}
          </button>
        </div>
      </div>
    </div>
  );
};
